import { useMemo, useState } from 'react'
import type { CatalogCard } from '@/types/card'
import { reorderFavorites } from '@/features/favorites/store'
import { useSortieren, type Griff } from '@/features/favorites/useSortieren'
import { CardTile } from './CardTile'
import { CardRow } from './CardRow'
import { IconGriff, IconKacheln, IconListe } from './icons'

/**
 * Favoriten der Startseite — als Kachelraster oder als Liste, sortierbar durch Ziehen.
 *
 * **Zur Sortierung:** Griffe erscheinen nur, wenn „Sortieren" eingeschaltet ist. Der
 * Grund ist der Einsatz: Mit Handschuhen gerät eine gehaltene Geste leicht außer
 * Kontrolle, und ein versehentlich verschobener Favorit fällt erst auf, wenn man ihn
 * an seinem Platz sucht. Der ausdrückliche Modus schließt das aus, ohne das Ziehen
 * umständlicher zu machen — wer sortieren will, tut genau das.
 *
 * Gezogen wird ausschließlich am Griff, nicht an der ganzen Kachel. Sonst stünde jede
 * Berührung zwischen zwei Deutungen: Karte öffnen oder Karte verschieben.
 *
 * **Zu den beiden Ansichten:** Kacheln zeigen das Vorschaubild — wer eine Karte kennt,
 * erkennt sie am Layout schneller als am Titel. Die Liste zeigt mehr Karten auf einmal
 * und dazu Untertitel und Zustand. Ab etwa einem Dutzend Favoriten trägt die Kachel
 * nicht mehr; welche Grenze gilt, weiß nur, wer die App benutzt, deshalb ist es eine
 * Einstellung und keine Automatik.
 */

type Ansicht = 'kacheln' | 'liste'

const ANSICHT_SPEICHER = 'taschenkarten:favoriten-ansicht'

function leseAnsicht(): Ansicht {
  try {
    return localStorage.getItem(ANSICHT_SPEICHER) === 'liste' ? 'liste' : 'kacheln'
  } catch {
    // Privater Modus ohne Speicherzugriff — dann eben die Voreinstellung.
    return 'kacheln'
  }
}

function merkeAnsicht(ansicht: Ansicht): void {
  try {
    localStorage.setItem(ANSICHT_SPEICHER, ansicht)
  } catch {
    // Kein Speicherzugriff: Die Wahl gilt dann nur für diese Sitzung.
  }
}

export function Favoriten({ cards }: { cards: readonly CatalogCard[] }) {
  const [ansicht, setAnsicht] = useState<Ansicht>(leseAnsicht)
  const [sortiermodus, setSortiermodus] = useState(false)

  const ids = useMemo(() => cards.map((card) => card.id), [cards])
  const nachId = useMemo(() => new Map(cards.map((card) => [card.id, card])), [cards])

  const sortierung = useSortieren(
    ids,
    (neu) => void reorderFavorites(neu),
    (id) => nachId.get(id)?.title ?? id,
  )

  const geordnet = sortierung.reihenfolge.flatMap((id) => nachId.get(id) ?? [])

  const wähle = (neu: Ansicht) => {
    setAnsicht(neu)
    merkeAnsicht(neu)
  }

  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <h2 className="label">Favoriten</h2>

        <div className="flex items-center gap-1">
          <Umschalter
            aktiv={ansicht === 'kacheln'}
            onClick={() => wähle('kacheln')}
            label="Als Kacheln anzeigen"
          >
            <IconKacheln className="size-5" />
          </Umschalter>
          <Umschalter
            aktiv={ansicht === 'liste'}
            onClick={() => wähle('liste')}
            label="Als Liste anzeigen"
          >
            <IconListe className="size-5" />
          </Umschalter>

          {cards.length > 1 && (
            <button
              type="button"
              onClick={() => setSortiermodus((v) => !v)}
              aria-pressed={sortiermodus}
              className={
                'ml-2 min-h-9 px-2 text-xs font-semibold uppercase tracking-wider ' +
                'transition-colors duration-100 ' +
                (sortiermodus ? 'text-accent' : 'text-muted hover:text-ink')
              }
            >
              {sortiermodus ? 'Fertig' : 'Sortieren'}
            </button>
          )}
        </div>
      </div>

      {sortiermodus && (
        <p className="mt-2 text-xs text-muted">
          Am Griff ziehen. Mit der Tastatur: Leertaste nimmt auf, die Pfeiltasten
          verschieben, die Leertaste legt wieder ab.
        </p>
      )}

      {/* Die Sprachausgabe begleitet das Verschieben — ohne sie wäre für einen blinden
          Nutzer nicht wahrnehmbar, dass sich überhaupt etwas bewegt hat. */}
      <p aria-live="polite" className="sr-only">
        {sortierung.ansage}
      </p>

      {ansicht === 'kacheln' ? (
        <div className={`mt-3 grid grid-cols-2 gap-3 ${sortiermodus ? '' : 'stack-in'}`}>
          {geordnet.map((card) => (
            <Eintrag key={card.id} sortierung={sortierung} card={card} sortiermodus={sortiermodus}>
              <div className="relative">
                <CardTile card={card} />
                {sortiermodus && (
                  <Griffknopf griff={sortierung.griff(card.id)} titel={card.title} überlagernd />
                )}
              </div>
            </Eintrag>
          ))}
        </div>
      ) : (
        <div className="mt-3 border-t border-line">
          {geordnet.map((card) => (
            <Eintrag key={card.id} sortierung={sortierung} card={card} sortiermodus={sortiermodus}>
              <div className="flex items-stretch bg-surface">
                {sortiermodus && (
                  <Griffknopf griff={sortierung.griff(card.id)} titel={card.title} />
                )}
                <div className="min-w-0 flex-1">
                  <CardRow card={card} />
                </div>
              </div>
            </Eintrag>
          ))}
        </div>
      )}
    </>
  )
}

/**
 * Hülle um einen Eintrag: hält die Messung für das Sortieren und legt den bewegten
 * Eintrag unter den Finger.
 */
function Eintrag({
  sortierung,
  card,
  sortiermodus,
  children,
}: {
  sortierung: ReturnType<typeof useSortieren>
  card: CatalogCard
  sortiermodus: boolean
  children: React.ReactNode
}) {
  const bewegt = sortierung.aktiv === card.id
  return (
    <div
      ref={sortierung.merke(card.id)}
      style={
        bewegt
          ? {
              // Angehoben statt durchscheinend: Eine halbdurchsichtige Kachel ließe die
              // darunterliegende durchschimmern, und man läse zwei Titel übereinander.
              transform: `translate(${sortierung.versatz.x}px, ${sortierung.versatz.y}px) scale(1.03)`,
              // Über allen anderen, sonst schöbe sich der bewegte Eintrag unter seine
              // Nachbarn, sobald er sie überlappt.
              zIndex: 30,
              position: 'relative',
            }
          : undefined
      }
      className={
        // Nur die ruhenden Einträge weichen weich aus; der bewegte folgt dem Finger
        // ohne Verzögerung, sonst liefe er ihm hinterher.
        bewegt
          ? 'shadow-card [&_a]:bg-surface'
          : sortiermodus
            ? 'transition-transform duration-150'
            : ''
      }
    >
      {children}
    </div>
  )
}

/**
 * Der Ziehgriff. 56 px, wie jede Bedienfläche dieser App — mit Handschuhen ist ein
 * schmaler Streifen am Rand nicht zu treffen.
 */
function Griffknopf({
  griff,
  titel,
  überlagernd = false,
}: {
  griff: Griff
  titel: string
  überlagernd?: boolean
}) {
  return (
    <button
      type="button"
      {...griff}
      aria-label={`„${titel}" verschieben`}
      className={
        'tap flex shrink-0 items-center justify-center text-muted ' +
        'transition-colors duration-100 hover:text-accent ' +
        (überlagernd
          ? // Auf der Kachel: oben links über dem Vorschaubild, mit eigener Fläche,
            // damit der Griff auf jedem Kartenkopf zu sehen ist.
            'absolute left-0 top-0 z-10 rounded-card rounded-br-none bg-surface/95 shadow-card'
          : 'border-b border-line')
      }
    >
      <IconGriff className="size-6" />
    </button>
  )
}

function Umschalter({
  aktiv,
  onClick,
  label,
  children,
}: {
  aktiv: boolean
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={aktiv}
      aria-label={label}
      className={
        'flex min-h-9 min-w-9 items-center justify-center rounded-chip border ' +
        'transition-colors duration-100 ' +
        (aktiv
          ? 'border-accent bg-accent text-accent-ink'
          : 'border-transparent text-muted hover:text-ink')
      }
    >
      {children}
    </button>
  )
}
